import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const wordsFromString = (s) => (s || "").split(" ");

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.04, delayChildren: 0.15 } },
};
const word = {
  hidden: { y: 36, opacity: 0, filter: "blur(6px)" },
  show: { y: 0, opacity: 1, filter: "blur(0px)", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

// Truncate an article title for the small photo-caption overlay
const truncate = (s, n = 90) => {
  if (!s) return "";
  return s.length > n ? s.slice(0, n).trim() + "…" : s;
};

function HeroSkeleton() {
  // Neutral placeholder — NO ghost image, NO stale text
  return (
    <section id="narmadapuram" data-testid="hero-skeleton" className="relative bg-[#F4F4F0] border-b border-[#0F0F0F]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-6 md:pt-12 pb-10 md:pb-16 grid grid-cols-12 gap-6 md:gap-10">
        <div className="col-span-12 lg:col-span-5 flex flex-col order-2 lg:order-1 space-y-5">
          <div className="h-3 w-24 bg-[#0F0F0F]/10 rounded" />
          <div className="h-10 w-full bg-[#0F0F0F]/10 rounded" />
          <div className="h-10 w-5/6 bg-[#0F0F0F]/10 rounded" />
          <div className="h-4 w-full bg-[#0F0F0F]/10 rounded mt-4" />
          <div className="h-4 w-4/5 bg-[#0F0F0F]/10 rounded" />
        </div>
        <div className="col-span-12 lg:col-span-7 order-1 lg:order-2">
          <div className="relative w-full aspect-[4/3] bg-[#0F0F0F]/8 border border-[#0F0F0F]/15" />
        </div>
      </div>
    </section>
  );
}

export default function Hero({ hero, teasers }) {
  // Point 2 — NO fallback to demo river-ghat image; render skeleton until DB data arrives.
  const [imgReady, setImgReady] = useState(false);

  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);

  // Preload the hero image before showing so the browser never paints a broken/old ghost
  useEffect(() => {
    setImgReady(false);
    if (!hero?.img) return;
    const img = new Image();
    img.onload = () => setImgReady(true);
    img.onerror = () => setImgReady(true); // still render; alt text will show
    img.src = hero.img;
  }, [hero?.img]);

  if (!hero || !hero.headline) {
    return <HeroSkeleton />;
  }

  const H = hero;
  const TZ = (teasers && teasers.length ? teasers.slice(0, 3) : []).map((t) => ({
    kicker: t.kicker,
    title: t.title,
    slug: t.slug,
  }));

  // Point 1 — caption = actual article title (dynamic), NOT hardcoded quote
  const captionText = truncate(H.title || H.headline || "");

  return (
    <section id="narmadapuram" ref={ref} data-testid="hero-section" className="relative bg-[#F4F4F0] border-b border-[#0F0F0F]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-6 md:pt-12 pb-10 md:pb-16 grid grid-cols-12 gap-6 md:gap-10">
        <div className="col-span-12 lg:col-span-5 flex flex-col order-2 lg:order-1">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="eyebrow text-[#D92525] mb-5 flex items-center gap-3">
            <span className="inline-block w-8 h-[2px] bg-[#D92525]" />
            {H.eyebrow}
          </motion.div>

          <motion.h1 variants={container} initial="hidden" animate="show"
            className="font-serif-dev font-black leading-[1.08] tracking-tight text-[32px] sm:text-[44px] md:text-[58px] lg:text-[62px] xl:text-[72px] text-[#0F0F0F]">
            {wordsFromString(H.headline).map((w, i) => (
              <motion.span key={i} variants={word} className="inline-block mr-[0.22em] will-change-transform">
                {w}
              </motion.span>
            ))}
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.7 }}
            className="mt-6 md:mt-8 font-sans-dev text-[16px] md:text-[18px] leading-relaxed text-[#4A4A4A] max-w-[560px]">
            {H.dek}
          </motion.p>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-8 flex flex-wrap items-center gap-5">
            <a data-testid="hero-read-cta" href={H.slug ? `/news/${H.slug}` : "#"} className="pill-btn font-sans-dev text-[12px]">
              पूरी रिपोर्ट पढ़ें <ArrowUpRight size={16} />
            </a>
            <span className="font-sans-dev text-[12px] uppercase tracking-[0.22em] text-[#4A4A4A]">{H.meta}</span>
          </motion.div>

          {TZ.length > 0 && (
            <div className="mt-10 md:mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-[#0F0F0F]/20 pt-6">
              {TZ.map((t, i) => (
                <motion.a key={i} href={t.slug ? `/news/${t.slug}` : "#"} data-testid={`hero-teaser-${i}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 + i * 0.1, duration: 0.6 }} className="block group">
                  <div className="eyebrow text-[#D92525] mb-2">{t.kicker}</div>
                  <div className="font-serif-dev font-bold text-[15px] leading-snug headline-link">{t.title}</div>
                </motion.a>
              ))}
            </div>
          )}
        </div>

        <div className="col-span-12 lg:col-span-7 order-1 lg:order-2">
          {/* Point 3 — consistent 4:3 aspect; object-contain + black bg so embedded text is never cropped */}
          <div className="relative w-full aspect-[4/3] overflow-hidden bg-[#0A0A0A]">
            <div className="absolute inset-0 border border-[#0F0F0F] pointer-events-none z-20" />
            {imgReady && (
              <motion.div style={{ y }} className="absolute inset-0">
                <img data-testid="hero-image" src={H.img} alt={H.title || "मुख्य समाचार"} className="w-full h-full object-contain" />
              </motion.div>
            )}
            <div className="absolute inset-0 z-10 pointer-events-none"
              style={{ background: "radial-gradient(ellipse at 50% 60%, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 100%)" }} />
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 z-10 flex items-end justify-between gap-4">
              <span data-testid="hero-caption" className="font-serif-dev italic text-white/95 text-[13px] md:text-[15px] leading-snug flex-1 min-w-0 line-clamp-2">
                {captionText}
              </span>
              <span className="font-sans-dev text-[10px] tracking-[0.28em] uppercase text-white/70 shrink-0">फोटो · देनवा</span>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-[#0F0F0F]">
        <div className="overflow-hidden">
          <div className="whitespace-nowrap flex items-center py-6 md:py-8 will-change-transform" style={{ animation: "dp-slow 60s linear infinite" }}>
            {[...Array(4)].map((_, i) => (
              <span key={i} className="font-serif-dev italic text-[#0F0F0F]/85 text-[54px] md:text-[88px] leading-none px-8">
                देनवा · नर्मदापुरम · मध्यप्रदेश · भरोसा · देनवा · नर्मदापुरम · मध्यप्रदेश · भरोसा
              </span>
            ))}
          </div>
        </div>
      </div>
      <style>{`@keyframes dp-slow { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
    </section>
  );
}
